import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import liste from '@/changelog.json'

/**
 * Was seit dem letzten Update neu ist: die Karte oben im Panel.
 *
 * Das Panel aktualisiert sich nach einem Deploy selbst (useVersionWatch). Was
 * danach anders ist, erfuhr bisher niemand. Dieselbe Lösung haben miraBOOK und
 * miraMENU seit Ende August 2026 (booking-Repo, admin.js `showChangelog`), und
 * die Regeln sind hier dieselben. Zwei verschiedene Lösungen für dieselbe Sache
 * zu pflegen ist genau der Fehler, den die Repo-Aufteilung vermeiden sollte.
 *
 * DIE LISTE: `src/changelog.json`, nur `{ id, date }`, neueste zuerst. Die Sätze
 * stehen in den Sprachdateien unter `changelog.entries.<id>`. Wer eine sichtbare
 * Neuerung ausliefert, schreibt beides; `v2/tools/changelog-pruefen.php` prüft
 * es vor jedem Deploy.
 *
 * WAS GEZEIGT WIRD: alle Einträge, deren Datum nach dem gemerkten Stand liegt,
 * höchstens vier. Weg erst beim Ausblenden. Beim allerersten Öffnen bewusst
 * nichts, sonst käme einmalig die ganze Liste.
 *
 * ZWEI ABWEICHUNGEN VON BOOKING, beide mit Grund:
 *   - Der Stand ist die Uhrzeit beim Merken, nicht die Bauzeit. Eine Bauzeit im
 *     Paket änderte bei jedem Bau den Vite-Hash, und useVersionWatch hielte
 *     jeden Deploy für eine neue Fassung, auch ohne geänderten Code.
 *   - Der Erstbesuch wird auch bei leerer Liste gemerkt. Booking kehrt bei
 *     leerer Liste vorher um; hier hätte das den ersten echten Eintrag für alle
 *     unsichtbar gemacht, weil er dann selbst als Erstbesuch gälte.
 *
 * Gemerkt wird JE PERSON (`miracms.neuerungen:<benutzer>`), so wie Booking je
 * Betrieb trennt: Wer ausblendet, blendet nur für sich aus, auch wenn sich zwei
 * Zugänge einen Rechner teilen.
 */

export interface Eintrag {
  id: string
  date: string
}

export interface FrischerEintrag {
  id: string
  ts: number
  schluessel: string
}

/** Eine Meldung, keine Versionsgeschichte. */
export const HOECHSTENS = 4

/**
 * Nur, was ein Schlüssel sein darf, kommt durch. Dieselben Regeln wie
 * `miraChangelogEntries()` in booking; Unbrauchbares fällt still weg, laut wird
 * es erst in der Prüfung vor dem Deploy.
 */
export function gueltigeEintraege(roh: unknown): Eintrag[] {
  if (!Array.isArray(roh)) return []
  const aus: Eintrag[] = []
  for (const e of roh) {
    if (typeof e !== 'object' || e === null) continue
    const { id, date } = e as Record<string, unknown>
    if (typeof id !== 'string' || typeof date !== 'string') continue
    if (!/^[a-z0-9-]{1,60}$/.test(id) || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue
    aus.push({ id, date })
  }
  return aus
}

/**
 * Das Datum eines Eintrags als Unix-Zeit. Bewusst UTC-Mitternacht wie in
 * booking: Der gemerkte Stand liegt immer später am Tag.
 */
export function zeitstempel(e: Eintrag): number {
  const ms = Date.parse(`${e.date}T00:00:00Z`)
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0
}

/** Was seit `gesehen` dazukam und einen Satz hat. */
export function frischeEintraege(
  eintraege: readonly Eintrag[],
  gesehen: number,
  hatSatz: (schluessel: string) => boolean,
): FrischerEintrag[] {
  return eintraege
    .filter((e) => zeitstempel(e) > gesehen)
    .slice(0, HOECHSTENS)
    .map((e) => ({ id: e.id, ts: zeitstempel(e), schluessel: `changelog.entries.${e.id}` }))
    .filter((x) => hatSatz(x.schluessel))
}

function speicherSchluessel(benutzer: string): string {
  return `miracms.neuerungen:${benutzer}`
}

function gelesen(benutzer: string): number {
  try {
    return Number(localStorage.getItem(speicherSchluessel(benutzer))) || 0
  } catch {
    // Privates Fenster oder gesperrter Speicher: dann eben nie etwas zeigen.
    return 0
  }
}

function merken(benutzer: string, ts: number): void {
  try {
    localStorage.setItem(speicherSchluessel(benutzer), String(ts))
  } catch {
    // Siehe gelesen(). Die Karte verschwindet trotzdem, nur eben nicht dauerhaft.
  }
}

function jetzt(): number {
  return Math.floor(Date.now() / 1000)
}

export function useNeuerungen(quelle: unknown = liste) {
  const { te, locale } = useI18n()
  const { user } = useAuth()
  const eintraege = gueltigeEintraege(quelle)

  /** Gäste (Kommentar-Link) sehen das Panel nicht als Werkzeug, also auch keine Neuerungen. */
  const benutzer = computed(() =>
    user.value && user.value.role !== 'gast' ? user.value.username : null,
  )

  const gesehen = ref<number | null>(null)

  watch(
    benutzer,
    (name) => {
      if (!name) {
        gesehen.value = null
        return
      }
      let stand = gelesen(name)
      if (!stand) {
        stand = jetzt()
        merken(name, stand)
      }
      gesehen.value = stand
    },
    { immediate: true },
  )

  /* Ohne Satz stünde hier ein roher Schlüssel. Deutsch ist die Rückfallsprache
     (locales/index.ts), ein Satz dort reicht also zum Anzeigen. */
  const neu = computed<FrischerEintrag[]>(() => {
    void locale.value
    if (gesehen.value === null) return []
    return frischeEintraege(eintraege, gesehen.value, (k) => te(k) || te(k, 'de'))
  })

  function ausblenden(): void {
    const name = benutzer.value
    if (!name) return
    /* Das Größere von jetzt und jüngstem gezeigten Eintrag, wie in booking:
       sonst käme ein Eintrag wieder, dessen Datum später liegt als die Uhr
       dieses Rechners. */
    const stand = Math.max(jetzt(), ...neu.value.map((x) => x.ts))
    merken(name, stand)
    gesehen.value = stand
  }

  return { neu, ausblenden }
}
